/** Configs do PORTAL do cliente: reaproveitam os do backoffice trocando o basePath
 *  (rotas /portal/...). O escopo por cliente é do backend (JWT); o form esconde o
 *  seletor de cliente sozinho. Usuários é bespoke (só sub-operadores, sem papel/cliente). */
/* eslint-disable react-refresh/only-export-components -- config co-loca o componente Fields */
import { StatusChip } from '../../../shared/components/StatusChip'
import { IdentidadeSection } from '../../usuarios/IdentidadeSection'
import {
  usuarioParaBody, usuarioParaForm, usuarioSchema, usuarioVazio,
  type UsuarioFormValues,
} from '../../usuarios/usuarioForm'
import { portalConfig, type ResourceConfig } from '../config'
import { usersResource } from '../resources'
import { placesConfig, fleetConfig, personnelConfig, assetsConfig } from './gestao'
import type { Usuario } from '../../../core/api/types'

export const portalPlacesConfig = portalConfig(placesConfig, { basePath: '/portal/cadastros/places' })
export const portalFleetConfig = portalConfig(fleetConfig, { basePath: '/portal/cadastros/fleet' })
export const portalPersonnelConfig = portalConfig(personnelConfig, { basePath: '/portal/cadastros/personnel' })
export const portalAssetsConfig = portalConfig(assetsConfig, { basePath: '/portal/cadastros/assets' })

// ── Usuários do portal (só sub-usuários do próprio cliente) ───────────
// O FORMULÁRIO vive em features/usuarios/UsuarioFormPage: seis abas de vinculação
// não cabem na casca genérica. Este config responde pela LISTA, e reaproveita o
// mesmo modelo da tela para os dois nunca divergirem.

/** O ResourceConfig exige `Fields`. Apontar para a seção real da tela, em vez de
 *  um esqueleto morto, mantém o tipo satisfeito com código vivo e preserva a opção
 *  de voltar ao formulário genérico. */
const PortalIdentidadeFields = ({ editing }: { editing: boolean }) => (
  <IdentidadeSection editando={editing} variante="portal" />
)

export const portalUsuariosConfig: ResourceConfig<Usuario> = {
  title: 'Usuários', eyebrow: 'Portal',
  singular: 'usuário', basePath: '/portal/usuarios', labelOf: (u) => u.name, resource: usersResource,
  searchable: true, schema: usuarioSchema, Fields: PortalIdentidadeFields,
  createPermission: 'users.manage', editPermission: 'users.manage', deletePermission: 'users.manage',
  columns: [
    { key: 'name', header: 'Nome' },
    { key: 'email', header: 'E-mail' },
    { key: 'phone', header: 'Telefone', render: (u) => u.phone ?? '—' },
    {
      key: 'is_active', header: 'Acesso',
      render: (u) =>
        u.is_blocked ? <StatusChip label="Bloqueado" tone="error" />
        : !u.is_active ? <StatusChip label="Inativo" tone="neutral" />
        : u.primeiro_acesso_pendente ? <StatusChip label="Aguardando 1º acesso" tone="warning" />
        : <StatusChip label="Ativo" tone="success" />,
    },
  ],
  emptyForm: usuarioVazio('portal'),
  toForm: usuarioParaForm,
  // role/customer_uuid são forçados no backend (customer_operator + o próprio cliente).
  toBody: (v) => usuarioParaBody(v as UsuarioFormValues, 'portal'),
}

/** Cadastros do portal (uma entrada = rotas lista/new/:uuid sob /portal). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PORTAL_RESOURCE_CONFIGS: ResourceConfig<any>[] = [
  portalPlacesConfig,
  portalFleetConfig,
  portalPersonnelConfig,
  portalAssetsConfig,
  portalUsuariosConfig,
]
